import React, { useMemo, useRef } from 'react';
import { withStyles, Typography } from '@material-ui/core';
import { connect } from 'react-redux';
import { hasPermission, hasAtLeastOnePermission } from '../../sc-utilities';
import MobileProductAuditRecordList from "./MobileProductAuditRecordList";

const styles = theme => ({
    root: {
        width: `100%`,
        height: 'auto',
        marginTop: '-1%',
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            width: '100%'
        }
    },
    "@global": {
        "[class*='statusActive']": {
            color: theme.palette.success.dark
        },
        "[class*='statusInactive']": {
            color: theme.palette.error.dark
        },
        "[class*='button']": {
            textTransform: 'none',
            textDecoration: 'none'
        }
    },
    button: {
        textTransform: 'none',
        textDecoration: 'none'
    },
    statusActive: {
        color: theme.palette.success.dark
    },
    statusInactive: {
        color: theme.palette.error.dark
    }
});

const MobileProductAuditRecord = props => {
    const tableRef = useRef();
    const { classes, translations, permissions, editable, columns, productID } = props;
    const visible = columns && columns['AuditRecord'];

    const parseIntValues = (value) => {
        switch(value){
            case 0:
                return translations["no"] || 'No';
            case 1:
                return translations["yes"] || 'Yes';
            case -1:
                return translations["failed"] || 'Failed';
            case -2:
                return translations["pending"] || 'Pending';
            case -3:
                return translations["no_service"] || 'No service';
            case -4:
                return translations["no_answer"] || 'No answer';
            case -5:
                return translations["no_balance"] || 'No balance';
            case null:
            default:
                return translations["short_not_assigned"] || "n/a";
        }
    }

    const parseStringValues = (value) => {
        switch(value){
            case '0':
                return translations["no"] || 'No'
            case '-1':
                return translations["failed"] || 'Failed';
            case '-2':
                return translations["pending"] || 'Pending';
            case '-3':
                return translations["no_service"] || 'No service';
            case '-4':
                return translations["no_answer"] || 'No answer';
            case '-5':
                return translations["no_balance"] || 'No balance';
            case null:
                return translations["short_not_assigned"] || "n/a";
            default:
                return value;
        }
    }

    const tableColumns = useMemo(() => {
        return [
            {
                title: translations["carrier"] || 'Carrier', field: 'Carrier',
                hidden: visible ? visible['Carrier'] : false,
                filtering: false,
                render: rowData => <Typography variant={'caption'}>{parseStringValues(rowData.Carrier)}</Typography>
            },
            {
                title: translations["sim_lock"] || 'SIM Lock', field: 'SimLock',
                hidden: visible ? visible['SimLock'] : false,
                filtering: false,
                render: rowData => <Typography variant={'caption'}>{parseIntValues(rowData.SimLock)}</Typography>
            },
            {
                title: translations["find_my_iphone"] || 'Find my iPhone/FRP', field: 'FindMyIphone',
                hidden: visible ? visible['FindMyIphone'] : false,
                filtering: false,
                render: rowData => <Typography variant={'caption'}>{parseIntValues(rowData.FindMyIphone)}</Typography>
            },
            {
                title: translations['carrier_lock'] || 'Carrier Lock', field: 'CarrierLock',
                hidden: visible ? visible['CarrierLock'] : false,
                filtering: false,
                render: rowData => <Typography variant={'caption'}>{parseIntValues(rowData.CarrierLock)}</Typography>
            },
            {
                title: 'MDM', field: 'MDM',
                hidden: visible ? visible['MDM'] : false,
                filtering: false,
                render: rowData => <Typography variant={'caption'}>{parseIntValues(rowData.MDM)}</Typography>
            },
            {
                title: translations["stolen"] || 'Stolen', field: 'Stolen',
                hidden: visible ? visible['Stolen'] : false,
                filtering: false,
                render: rowData => <Typography variant={'caption'}>{parseIntValues(rowData.Stolen)}</Typography>
            },
            {
                title: translations["financed"] || 'Financed', field: 'Financed',
                hidden: visible ? visible['Financed'] : false,
                filtering: false,
                render: rowData => <Typography variant={'caption'}>{parseIntValues(rowData.Financed)}</Typography>
            },
            {
                title: translations["warranty_status"] || 'Warranty Status', field: 'WarrantyStatus',
                hidden: visible ? visible['WarrantyStatus'] : false,
                filtering: false,
                render: rowData => <Typography variant={'caption'}>{parseStringValues(rowData.WarrantyStatus)}</Typography>
            },
            {
                title: translations["repairs_and_service_coverage"] || 'Repairs And Service Coverage', field: 'RepairsAndServiceCoverage',
                hidden: visible ? visible['RepairsAndServiceCoverage'] : false,
                filtering: false,
                render: rowData => <Typography variant={'caption'}>{parseIntValues(rowData.RepairsAndServiceCoverage)}</Typography>
            },
            {
                title: translations["repairs_and_service_expiration_date"] || 'Repairs And Service Expiration Date', field: 'RepairsAndServiceExpirationDate',
                hidden: visible ? visible['RepairsAndServiceExpirationDate'] : false,
                filtering: false,
                render: rowData => <Typography variant={'caption'}>{parseStringValues(rowData.RepairsAndServiceExpirationDate)}</Typography>
            },
            {
                title: translations["applecare_eligible"] || 'AppleCare Eligible', field: 'AppleCareEligible',
                hidden: visible ? visible['AppleCareEligible'] : false,
                filtering: false,
                render: rowData => <Typography variant={'caption'}>{parseIntValues(rowData.AppleCareEligible)}</Typography>
            },
            {
                title: translations["estimated_purchase_date"] || 'Estimated Purchase Date', field: 'EstimatedPurchaseDate',
                hidden: visible ? visible['EstimatedPurchaseDate'] : false,
                filtering: false,
                render: rowData => <Typography variant={'caption'}>{parseStringValues(rowData.EstimatedPurchaseDate)}</Typography>
            },
            {
                title: translations["replaced_device"] || 'Replaced Device', field: 'ReplacedDevice',
                hidden: visible ? visible['ReplacedDevice'] : false,
                filtering: false,
                render: rowData => <Typography variant={'caption'}>{parseIntValues(rowData.ReplacedDevice)}</Typography>
            },
            {
                title: translations["loaner_device"] || 'Loaner Device', field: 'LoanerDevice',
                hidden: visible ? visible['LoanerDevice'] : false,
                filtering: false,
                render: rowData => <Typography variant={'caption'}>{parseIntValues(rowData.LoanerDevice)}</Typography>
            },
            {
                title: translations["a_number"] || 'ANumber', field: 'ANumber',
                hidden: visible ? visible['ANumber'] : false,
                filtering: false,
                render: rowData => <Typography variant={'caption'}>{parseStringValues(rowData.ANumber)}</Typography>
            },
            {
                title: translations["external_name"] || 'External name', field: 'ExternalName',
                hidden: visible ? visible['ExternalName'] : false,
                filtering: false,
                render: rowData => <Typography variant={'caption'}>{rowData.DeviceDataApiID ? rowData.DeviceDataApi?.Name : ""}</Typography>
            },
            {
                title: translations["external_type"] || 'External type', field: 'ExternalType',
                hidden: visible ? visible['ExternalType'] : false,
                filtering: false,
                render: rowData => <Typography variant={'caption'}>{rowData.DeviceDataApiID ? rowData.DeviceDataApi?.Type : ""}</Typography>
            },
            {
                title: translations["type"] || 'Type', field: 'Type',
                hidden: visible ? visible['Type'] : false,
                filtering: false,
                render: rowData => <Typography variant={'caption'}>{rowData.DeviceDataApiID ? translations["external"] || "External" : translations["internal"] || "Internal"}</Typography>
            },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classes, translations, visible]);

    if (!hasAtLeastOnePermission(permissions, ["product_audit_record_delete"])) {
        tableColumns.shift();
    }

    return (
        <div className={classes.root}>
            {hasPermission(permissions, 'product_audit_record_list') &&
                <MobileProductAuditRecordList editable={editable} productID={productID} tableColumns={tableColumns} tableRef={tableRef} />}
        </div >
    );

}
const mapStateToProps = state => {
    return {
        permissions: state?.core?.login?.permissions || [],
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
    };
};


export default connect(
    mapStateToProps,
    null,
)(withStyles(styles, { withTheme: true })(MobileProductAuditRecord))
